import { ParentProxyType, StudentProxyType } from '../../../peekapak-types/DataProtocolTypes';
import { getDescriptiveDateFromEpoch, getPossessiveForm } from '../../GlobalFunctions';
import styles from '../../SCSS/ParentStatus.module.scss';

interface Props {
  statusOnly?: boolean;
  student: StudentProxyType;
  parentsList: ParentProxyType[] | undefined;
}

const ParentStatus = ({ statusOnly, student, parentsList }: Props) => {
  let studentPossessiveForm: string;

  if (!student || !student.firstName || !student.firstName.trim()) {
    studentPossessiveForm = 'Name not provided';
  } else {
    studentPossessiveForm = getPossessiveForm(student.firstName);
  }

  if (parentsList === undefined) {
    return <div className={styles['parent-status-not-provided']}>Parent information has not yet been provided</div>;
  }

  const getParentsLastName = (parent: ParentProxyType) => {
    if (parent.lastName === undefined || parent.lastName === 'null') return '';
    else if (parent.lastName && parent.lastName.charAt(parent.lastName.length - 1) === '=') return '';
    else if (parent.lastName && parent.lastName.startsWith('..encrypted')) return '';

    return parent.lastName;
  };

  return (
    <div>
      {parentsList.map((parent, index) => {
        const parentFirstName = parent.firstName === undefined ? '' : parent.firstName;
        const parentLastName = getParentsLastName(parent);
        const parentsEmail = parent.email === undefined ? 'Email not yet provided' : parent.email;
        const parentInformationText = `${studentPossessiveForm} Parent ${parentFirstName} ${parentLastName} - ${parentsEmail}`;
        let statusMessage = 'Invitation not yet sent.';
        let isParentConnected;

        if (parent.invitationStatus !== undefined) {
          switch (parent.invitationStatus.status) {
            case 'Sent':
              if (parent.invitationStatus.lastSendDate === undefined) {
                throw new Error('parent.invitationStatus.lastSendDate is undefined');
              } else {
                if (parent.consentStatus) {
                  statusMessage = `Invite resent ${getDescriptiveDateFromEpoch(parent.invitationStatus.lastSendDate)}.`;
                } else {
                  statusMessage = `Invite last sent ${getDescriptiveDateFromEpoch(
                    parent.invitationStatus.lastSendDate,
                  )}.`;
                }
              }
              break;
            case 'Accepted':
              isParentConnected = true;
              if (parent.invitationStatus.acceptedDate === undefined) {
                throw new Error('parent.invitationStatus.acceptedDate is undefined');
              } else {
                statusMessage = `Invitation accepted ${getDescriptiveDateFromEpoch(
                  parent.invitationStatus.acceptedDate,
                )}.`;
              }
              break;
            case 'Declined':
              if (parent.invitationStatus.declinedDate === undefined) {
                throw new Error('parent.invitationStatus.declinedDate is undefined');
              } else {
                statusMessage = `Invitation declined ${getDescriptiveDateFromEpoch(
                  parent.invitationStatus.declinedDate,
                )}.`;
              }
              break;
            case 'OptedOut':
              if (parent.invitationStatus.optOutDate === undefined) {
                throw new Error('parent.invitationStatus.optOutDate is undefined');
              } else {
                statusMessage = `Opted out of updates ${getDescriptiveDateFromEpoch(
                  parent.invitationStatus.optOutDate,
                )}.`;
              }
              break;
            case 'Opened':
              if (parent.invitationStatus.openedDate === undefined) {
                throw new Error('parent.invitationStatus.openedDate is undefined');
              } else {
                statusMessage = `Invitation opened ${getDescriptiveDateFromEpoch(parent.invitationStatus.openedDate)}.`;
              }
              break;
            default:
              statusMessage = ''; // should never reach this
          }
        }

        return (
          <div key={index}>
            {!statusOnly && <div className={styles['parent-status-text']}>{parentInformationText}</div>}
            <div
              className={`${styles['invitation-status-text']} ${
                isParentConnected ? styles['invitation-accepted'] : ''
              }`}
            >
              {statusMessage}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ParentStatus;
